import React, { useEffect, useState, useCallback, ChangeEvent } from "react";
import { Socket } from "socket.io-client";
import ICategory from "@models/ICategory";
import ICatalog_Item_Attribute from "@models/ICatalog_Item_Attribute";
import IAttribute from "@models/IAttribute";
import IGetAttributesDTO from "@models/DTO/IGetAttributesDTO";
import IOnGetAttributes from "@models/DTO/IOnGetAttributes";
import EndpointsEnum from "@models/EndpointsEnum";
import IUserUI from "@models/IUserUI";
import ISupplierModel from "@models/ISupplierModel";
import AttributeTypeEnum from "@models/AttributeTypeEnum";
import OwSelectOption from "@components/shared/OwSelectOption/OwSelectOption";
import OwCheckbox from "@components/shared/OwCheckbox/OwCheckbox";
import OwDateInput from "@components/shared/OwDateInput/OwDateInput";
import { OwFnInput } from "@components/shared/OwFnInput/OwFnInput";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import { NumericFormat } from "react-number-format";
import { Moment } from "moment-timezone";
import "./CategoryItemCategoryAttributes.scss";
import { OwIconTooltip } from "@components/shared/OwIconTooltip/OwIconTooltip";

type Props = {
    socket: Socket;
    userModel: IUserUI;
    supplierModel: ISupplierModel;
    selectedCategory?: ICategory;
    selectedSubCategory?: ICategory;
    selectedSubSubCategory?: ICategory;
    selectedFinalCategory?: ICategory;
    catalogItemAttributes?: ICatalog_Item_Attribute[];
    onAttributesChange: (attributes: ICatalog_Item_Attribute[]) => void;
};

export const CategoryItemCategoryAttributes = (props: Props) => {
    const [categoryAttributes, setCategoryAttributes] = useState<IAttribute[]>([]);
    const [attributeValues, setAttributeValues] = useState<{ [key: number]: any }>({});
    const [loadingAttributes, setLoadingAttributes] = useState(false);

    const getAllSelectedCategories = useCallback((): ICategory[] => {
        const categories: ICategory[] = [];
        if (props.selectedCategory) categories.push(props.selectedCategory);
        if (props.selectedSubCategory) categories.push(props.selectedSubCategory);
        if (props.selectedSubSubCategory) categories.push(props.selectedSubSubCategory);
        if (props.selectedFinalCategory) categories.push(props.selectedFinalCategory);
        return categories;
    }, [props.selectedCategory, props.selectedSubCategory, props.selectedSubSubCategory, props.selectedFinalCategory]);

    const getAttributes = useCallback(
        (category: ICategory) => {
            if (!category.guid) {
                props.onAttributesChange([]);
                setCategoryAttributes([]);
                return;
            }

            const payload: IGetAttributesDTO = {
                userGuid: props.userModel.guid,
                userToken: props.userModel.token,
                supplierGuid: props.supplierModel.guid,
                categoryGuid: category.guid,
            };

            props.socket.emit(EndpointsEnum.CategoriesGetAttributes, payload);
        },
        [props.socket, props.userModel, props.supplierModel]
    );

    const onGetAttributes = useCallback(
        (data: IOnGetAttributes) => {
            const categories = getAllSelectedCategories();
            const matchingCategory = categories.find((cat) => cat.guid === data.categoryGuid);

            if (matchingCategory) {
                setCategoryAttributes((prevAttributes) => {
                    const existingIds = new Set(prevAttributes.map((attr) => attr.id));
                    const newAttributes = data.attributes.filter((attr) => !existingIds.has(attr.id));
                    return [...prevAttributes, ...newAttributes];
                });
            }
        },
        [getAllSelectedCategories]
    );

    useEffect(() => {
        props.socket.on(EndpointsEnum.CategoriesOnGetAttributes, onGetAttributes);

        return () => {
            props.socket.off(EndpointsEnum.CategoriesOnGetAttributes, onGetAttributes);
        };
    }, [props.socket, onGetAttributes]);

    useEffect(() => {
        setCategoryAttributes([]);
        setAttributeValues({});
        setLoadingAttributes(true);

        const categories = getAllSelectedCategories();

        categories.forEach((category) => {
            getAttributes(category);
        });

        setTimeout(() => setLoadingAttributes(false), 1000);
    }, [
        props.selectedCategory,
        props.selectedSubCategory,
        props.selectedSubSubCategory,
        props.selectedFinalCategory,
        getAttributes,
        getAllSelectedCategories,
    ]);

    useEffect(() => {
        if (props.catalogItemAttributes) {
            const values: { [key: number]: any } = {};
            props.catalogItemAttributes.forEach((attr) => {
                if (attr.attribute_id) {
                    values[attr.attribute_id] = attr.value;
                }
            });
            setAttributeValues(values);
        }
    }, [props.catalogItemAttributes]);

    const handleAttributeChange = (attributeId: number, value: any) => {
        const newValues = { ...attributeValues, [attributeId]: value };
        setAttributeValues(newValues);

        const updatedAttributes: ICatalog_Item_Attribute[] = categoryAttributes.map((attr) => {
            const existingAttr = props.catalogItemAttributes?.find((catAttr) => catAttr.attribute_id === attr.id);

            return {
                id: existingAttr?.id || null,
                catalog_item_id: existingAttr?.catalog_item_id || 0,
                catalog_id: existingAttr?.catalog_id || 0,
                attribute_id: attr.id,
                value: newValues[attr.id] !== undefined ? newValues[attr.id] : null,
                is_enabled: true,
                display_order: attr.display_order || 0,
                Attribute: attr,
                _isVirtual: !existingAttr || existingAttr._isVirtual === true,
            } as unknown as ICatalog_Item_Attribute;
        });

        props.onAttributesChange(updatedAttributes);
    };

    const renderAttributeInput = (attribute: IAttribute) => {
        const value = attributeValues[attribute.id];
        console.log("value", value);
        const isRequired = attribute.required === true;

        switch (attribute.type) {
            case AttributeTypeEnum.TEXT:
                return (
                    <OwFnInput
                        skipTranslate={true}
                        name={`attribute-${attribute.id}`}
                        value={value || ""}
                        fn={(e: React.ChangeEvent<HTMLInputElement>) => handleAttributeChange(attribute.id, e.target.value)}
                        placeholder={`Enter ${attribute.name}`}
                        isRequired={isRequired}
                    />
                );

            case AttributeTypeEnum.NUMBER:
                return (
                    <NumericFormat
                        className="form-control"
                        value={value || ""}
                        onValueChange={(values) => handleAttributeChange(attribute.id, values.floatValue)}
                        placeholder={`Enter ${attribute.name}`}
                        required={isRequired}
                        // thousandSeparator={true}
                        decimalScale={2}
                        fixedDecimalScale={false}
                    />
                );

            case AttributeTypeEnum.DROPDOWN: {
                const dropdownOptions = (attribute.options || []).map((opt: string | { value: string; label: string }) => {
                    if (typeof opt === "string") {
                        return { value: opt, label: opt };
                    }
                    return opt;
                });
                return (
                    <OwSelectOption
                        name={`attribute-${attribute.id}`}
                        options={dropdownOptions}
                        valueProp="value"
                        titleProp="label"
                        fn={(val: ChangeEvent<HTMLSelectElement>) => handleAttributeChange(attribute.id, val.target.value)}
                        selectedOption={value}
                        showBlankFirst={true}
                        isRequired={isRequired}
                        hasSelectedOptionProp={false}
                    />
                );
            }

            case AttributeTypeEnum.BOOLEAN:
                return (
                    <OwCheckbox
                        checked={!!value}
                        fn={(e: React.ChangeEvent<HTMLInputElement>) => handleAttributeChange(attribute.id, e.target.checked)}
                        label=""
                    />
                );

            case AttributeTypeEnum.DATE:
                return (
                    <OwDateInput
                        selectedDate={value ? new Date(value) : undefined}
                        fn={(date: Moment | null) => handleAttributeChange(attribute.id, date?.toDate())}
                    />
                );

            default:
                return null;
        }
    };

    if (!getAllSelectedCategories().length) {
        return null;
    }

    if (loadingAttributes) {
        return (
            <div className="CategoryItemCategoryAttributes">
                <div className="loading-message">Loading attributes...</div>
            </div>
        );
    }

    if (!categoryAttributes.length) {
        return null;
    }

    const sortedAttributes = [...categoryAttributes]
        // .filter((attr) => attr.is_hidden !== true)
        .sort((a, b) => {
            const orderA = a.display_order || 999;
            const orderB = b.display_order || 999;
            if (orderA !== orderB) return orderA - orderB;
            return (a.name || "").localeCompare(b.name || "");
        });

    return (
        <div className="CategoryItemCategoryAttributes">
            <h5 className="section-title">Category Attributes</h5>
            {sortedAttributes.map((attribute) => {
                const existingAttr = props.catalogItemAttributes?.find((attr) => attr.attribute_id === attribute.id);
                const isVirtual = !existingAttr || existingAttr._isVirtual === true;

                return (
                    <div key={attribute.id} className={`attribute-row ${isVirtual ? "virtual" : "persisted"}`}>
                        <div className="d-flex align-items-center">
                            <OwLabel
                                skipTranslate={true}
                                className="attribute-label"
                                title={`${attribute.required ? "* " : ""}${attribute.name}${isVirtual ? " (unset)" : ""}`}
                            />
                            {attribute.description ? (
                                <OwIconTooltip
                                    iconClassName={"fas fa-info-circle text-muted ms-2"}
                                    tooltipContent={attribute.description}
                                />
                            ) : null}
                        </div>
                        {renderAttributeInput(attribute)}
                    </div>
                );
            })}
        </div>
    );
};
